/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { SetStateAction, useEffect, useRef, useState } from "react";

import { useDetailsStore } from "@/stores/useDetailsStore";
import { useRedirectStore } from "@/stores/useRedirectStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";
import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

import {
  MenuAnchorEl,
  useStyles as sharedStyles,
  useMediaQueries
} from "../../../../../../../shared";
import MoreActionsButton from "../../../../../../../shared/components/buttons/MoreActionsButton";
import GlobalScheduleIcon from "../../../../../../Schedules/components/GlobalScheduleIcon/GlobalScheduleIcon";
import { specificGlobalSchedule } from "../../../../../../Schedules/functions";
import Separator from "../../../../../../Separator/Separator";
import SharedStatusBadge from "../../../../../../SharedStatusBadge/SharedStatusBadge";
import { LongTextTooltip } from "../../../../../../Tooltip/LongTextTooltip";
import useDetailsFunctions from "../../../../../hooks/useDetailsFunctions/useDetailsFunctions";
import { useStyles as useReusableDetailsStyles } from "../../../../../styles";
import { SingleDetailsAcordionItemprops } from "../../../../../types";
import { useCompanyCardFunctions } from "../../../hooks";
import { useStyles } from "../styles";

const SingleDetailsAcordionItem = ({
  route,
  title,
  totalCount,
  index,
  collapsable,
  children,
  titleInlineActions,
  anchorEl,
  menuWidth,
  companyCardStatus,
  setIsLoading,
  setIsActionsOpen,
  expandFirstItem = true,
  showFirstTopSeparator = true,
  fromDialog,
  addMaxHeight = true,
  showCollapseTooltips = true,
  cardData,
  showGlobalIcon
}: SingleDetailsAcordionItemprops) => {
  const { renderExpandIcon } = useDetailsFunctions();
  const { collapseArrow } = useReusableDetailsStyles();

  const { toSm, toMd, toLg } = useMediaQueries();

  const { loading: isTableLoading } = useTableStoreByRoute(route);
  const isDetailsLoading = useDetailsStore(state => state.isLoading);

  const takeToCompaniesCards = useRedirectStore(
    state => state.takeToCompaniesCards
  );

  const isFirstItemAllowedToCollapse =
    expandFirstItem && index === 0 && !takeToCompaniesCards;

  const [isOpen, setIsOpen] = useState<boolean>(isFirstItemAllowedToCollapse);
  const [isTitleOverflowing, setIsTitleOverflowing] = useState<boolean>(false);
  const [hasHighlight, setHasHighlight] = useState<boolean>(false);

  const { handleToggleCollapse } = useCompanyCardFunctions({
    isOpen,
    setIsOpen,
    setHasHighlight,
    cardData
  });

  const { pulsatingHighlight } = sharedStyles();

  const titleRef = useRef<HTMLDivElement>(null);
  const titleContainerRef = useRef<HTMLDivElement>(null);

  const {
    accordionItemTitleArea,
    collapsableItemContentDiv,
    accordionItemTitleContainer,
    notCollapsableItemContentDiv,
    accordionItemIconMargin
  } = useStyles({
    isOpen,
    fromDialog,
    addMaxHeight
  });

  const displayLoader = isDetailsLoading || isTableLoading;
  const collapsableCardContent = isOpen ? children : <></>;

  const titleWidthToSm = toSm && 200;
  const titleWidthToMd = toMd && 400;
  const titleWidthToLg = toLg && 200;

  const titleMaxWidth =
    titleWidthToSm || titleWidthToMd || titleWidthToLg || 300;

  useEffect(() => {
    titleRef.current &&
      setIsTitleOverflowing(
        titleRef.current.scrollWidth > titleRef.current?.clientWidth
      );

    setIsLoading?.(displayLoader);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    index,
    setIsLoading,
    displayLoader,
    titleRef.current?.scrollWidth,
    titleRef.current?.clientWidth,
    titleContainerRef.current?.clientWidth
  ]);

  const handleOpenActionsMenu = (e: {
    currentTarget: SetStateAction<MenuAnchorEl>;
  }) => {
    setIsActionsOpen?.(e.currentTarget);
  };

  const handleCloseActionsMenu = () => {
    setIsActionsOpen?.(null);
  };

  const titleStyleOverrides = {
    maxWidth: `${titleMaxWidth}px`,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  };

  return (
    <div
      id={cardData?.id ?? title}
      data-testid="single-details-accordion-item"
      css={css(hasHighlight ? pulsatingHighlight : undefined)}
    >
      {index < totalCount && showFirstTopSeparator && (
        <Separator color={colors.gray200} />
      )}

      <div css={css(accordionItemTitleContainer)} ref={titleContainerRef}>
        {/* title */}
        <div
          css={css([
            accordionItemTitleArea,
            companyCardStatus && accordionItemIconMargin
          ])}
        >
          {showGlobalIcon && (
            <GlobalScheduleIcon
              specificGlobalSchedule={specificGlobalSchedule(route)}
              colors={colors}
            />
          )}

          {companyCardStatus && (
            <SharedStatusBadge status={companyCardStatus} bubbleOnly />
          )}

          <LongTextTooltip
            title={title}
            open={isTitleOverflowing}
            customStyles={{
              "& .MuiTooltip-tooltip": { wordBreak: "break-all" }
            }}
          >
            <Typography
              variant="body2SemiBold"
              ref={titleRef}
              sx={titleStyleOverrides}
            >
              {title}
            </Typography>
          </LongTextTooltip>
        </div>

        {/* actions menu */}
        <div css={css(accordionItemTitleArea)}>
          {titleInlineActions && (
            <MoreActionsButton
              onMenuOpen={handleOpenActionsMenu}
              onCloseMenu={handleCloseActionsMenu}
              anchorEl={anchorEl || null}
              menuWidth={menuWidth}
            >
              {titleInlineActions}
            </MoreActionsButton>
          )}

          {/* arrow icon */}
          <div css={css(collapseArrow)}>
            {collapsable &&
              renderExpandIcon({
                handleToggleCollapse,
                isOpen,
                customStyle: {},
                hasTooltip: showCollapseTooltips
              })}
          </div>
        </div>
      </div>

      {/* Card content */}
      {collapsable && (
        <div css={css(collapsableItemContentDiv)}>{collapsableCardContent}</div>
      )}

      {!collapsable && (
        <div css={css(notCollapsableItemContentDiv)}>{children}</div>
      )}
    </div>
  );
};

export default SingleDetailsAcordionItem;
